import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CalendarCheck2, CalendarDays, Loader2, PartyPopper, Plane, PlayCircle, ScrollText, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { PageHeader } from "../../components/common";
import { api, errMsg, fmtDateTime, fmtDay } from "../../lib/api";
import { AttendanceRecordsTab } from "../../components/attendance/AttendanceRecordsTab";
import { LeaveRequestsTab } from "../../components/attendance/LeaveRequests";
import { HolidaysTab } from "../../components/attendance/HolidaysTab";
import { RulesSummaryCard } from "../../components/attendance/Rules";
import { AttendanceAuditTab } from "../../components/attendance/AuditTab";
import { useAttendanceRules } from "../../components/attendance/shared";

const TABS = ["records", "leave", "holidays", "rules", "audit"];

/** Phase 168 — Admin › Attendance: register + overrides, leave requests, holidays & working Sundays, rules, audit. */
export default function Attendance() {
  const { code } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "records";
  const setTab = (t) => setParams((p) => { const q = new URLSearchParams(p); q.set("tab", t); return q; }, { replace: true });
  const rules = useAttendanceRules(code);
  const [pending, setPending] = useState(null);
  const [runs, setRuns] = useState(null);
  const [running, setRunning] = useState(false);
  const [auditKey, setAuditKey] = useState(0);

  const refreshMeta = useCallback(() => {
    api.get(`/t/${code}/attendance/leave-requests/pending-count`).then((r) => setPending(r.data.pending)).catch(() => {});
    api.get(`/t/${code}/attendance/runs`, { params: { limit: 5 } }).then((r) => setRuns(r.data)).catch(() => {});
    setAuditKey((k) => k + 1);
  }, [code]);
  useEffect(() => { refreshMeta(); }, [refreshMeta]);

  const runFinalise = async () => {
    setRunning(true);
    try {
      const r = await api.post(`/t/${code}/attendance/finalise`, {});
      const d = r.data;
      toast.success(d.days.length ? `Finalised ${d.days.length} day${d.days.length === 1 ? "" : "s"} (${d.days.map(fmtDay).join(", ")})` : "Nothing to finalise — every past day is already final",
        { description: d.notified ? `${d.notified} collector notice${d.notified === 1 ? "" : "s"} sent for Half Day / Absent.` : undefined });
      refreshMeta();
    } catch (e) { toast.error(errMsg(e)); } finally { setRunning(false); }
  };
  const last = runs?.runs?.[0];

  return (
    <div data-testid="attendance-page">
      <PageHeader title="Attendance" subtitle="Daily attendance of tax collectors from their visits, collections and sign-ins — with overrides, field duty, holidays and leave">
        <div className="flex flex-wrap items-center gap-2">
          {runs && (
            <span className="text-xs text-muted-foreground" data-testid="attendance-finalise-status">
              {last ? <>Last finalised: <span className="font-medium text-foreground">{fmtDay(last.day)}</span> ({fmtDateTime(last.finished_at)})</> : "No day finalised yet"}
              {runs.due?.length > 0 && <Badge variant="outline" className="ml-2 text-[10px] border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))]" data-testid="attendance-finalise-due">{runs.due.length} day{runs.due.length === 1 ? "" : "s"} due</Badge>}
              <span className="ml-2">· nightly at {runs.finalise_time} IST</span>
            </span>
          )}
          <Button variant="outline" size="sm" className="h-9" onClick={runFinalise} disabled={running} data-testid="attendance-run-finalise" title="Runs the nightly finalisation now for every past day that is still open">
            {running ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1.5" />} Finalise due days
          </Button>
        </div>
      </PageHeader>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto" data-testid="attendance-tabs">
          <TabsTrigger value="records" data-testid="attendance-tab-records"><CalendarCheck2 className="h-4 w-4 mr-1.5" /> Records</TabsTrigger>
          <TabsTrigger value="leave" data-testid="attendance-tab-leave"><Plane className="h-4 w-4 mr-1.5" /> Leave requests {pending > 0 && <Badge className="ml-1.5 text-[10px] h-5 px-1.5" data-testid="attendance-leave-pending-badge">{pending}</Badge>}</TabsTrigger>
          <TabsTrigger value="holidays" data-testid="attendance-tab-holidays"><PartyPopper className="h-4 w-4 mr-1.5" /> Holidays</TabsTrigger>
          <TabsTrigger value="rules" data-testid="attendance-tab-rules"><SlidersHorizontal className="h-4 w-4 mr-1.5" /> Rules</TabsTrigger>
          <TabsTrigger value="audit" data-testid="attendance-tab-audit"><ScrollText className="h-4 w-4 mr-1.5" /> Audit log</TabsTrigger>
        </TabsList>
        <TabsContent value="records" className="mt-3"><AttendanceRecordsTab code={code} onChanged={refreshMeta} /></TabsContent>
        <TabsContent value="leave" className="mt-3"><LeaveRequestsTab code={code} onChanged={refreshMeta} /></TabsContent>
        <TabsContent value="holidays" className="mt-3"><HolidaysTab code={code} onChanged={refreshMeta} /></TabsContent>
        <TabsContent value="rules" className="mt-3 space-y-3">
          <RulesSummaryCard rules={rules} />
          <Card className="shadow-sm"><CardContent className="p-4 text-sm text-muted-foreground flex items-start gap-3">
            <CalendarDays className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden="true" />
            <div>
              <p className="font-medium text-foreground">How a day is decided</p>
              <ol className="list-decimal pl-4 mt-1 space-y-0.5 text-xs">
                <li>Holidays and Sundays (unless declared working) — nothing is recorded.</li>
                <li>Approved leave — the day is <b>Leave</b>.</li>
                <li>Requirement met (visits <b>or</b> collection) — <b>Present</b>; if the sign-in was late and the month's tolerated late sign-ins are used up — <b>Half Day</b>. Field duty days skip the late rule and show as <b>Field Visit</b>.</li>
                <li>Requirement not met — <b>Absent</b> (a pending leave request does not change this until it is approved).</li>
                <li>Your overrides always win and are logged with the reason; half day / absent collectors get an in-app notice with the date, status and reason.</li>
              </ol>
              <p className="text-xs mt-2">Thresholds and limits are set per municipality by the Super Admin (Tenant Configuration → Attendance Rules).</p>
            </div>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="audit" className="mt-3"><AttendanceAuditTab code={code} refreshKey={auditKey} /></TabsContent>
      </Tabs>
    </div>
  );
}
